import { Bug, Virus, Drop, DropHalf, Sun, Snowflake, Bandaids, Flask, Plant, DotsThreeCircle, Waves, PottedPlant } from "@phosphor-icons/react";

export const ISSUE_OPTIONS = [
  { value: "pest", label: "Pests", icon: Bug },
  { value: "disease", label: "Disease", icon: Virus },
  { value: "root", label: "Root rot", icon: Plant },
  { value: "overwatering", label: "Overwatering", icon: Waves },
  { value: "underwatering", label: "Underwatering", icon: DropHalf },
  { value: "sunburn", label: "Sunburn", icon: Sun },
  { value: "cold", label: "Cold damage", icon: Snowflake },
  { value: "transplant", label: "Transplant shock", icon: PottedPlant },
  { value: "physical", label: "Physical damage", icon: Bandaids },
  { value: "nutrient", label: "Nutrient deficiency", icon: Flask },
  { value: "other", label: "Other", icon: DotsThreeCircle },
];

export const SYMPTOMS = {
  pest: ["Fine webbing", "Sticky residue", "Tiny moving bugs", "White cottony fluff", "Stippled / speckled leaves", "Silver streaks", "Holes in leaves", "Fungus gnats in soil", "Brown scale bumps"],
  disease: ["Brown leaf spots", "Yellow halos", "Powdery white coating", "Black / mushy patches", "Wilting despite moist soil", "Foul smell", "Leaf drop"],
  root: ["Mushy stem base", "Soil smells sour", "Brown, soft roots", "Wilting despite moist soil", "Yellowing lower leaves", "Leaf drop"],
  overwatering: ["Yellowing lower leaves", "Soil stays wet", "Edema bumps", "Drooping, soft leaves", "Mold on soil", "Fungus gnats in soil"],
  underwatering: ["Crispy brown edges", "Curling leaves", "Bone-dry soil", "Drooping, limp leaves", "Soil pulling from pot"],
  sunburn: ["Bleached patches", "Crispy brown spots", "Faded color", "Burn on one side"],
  cold: ["Black / translucent leaves", "Mushy patches", "Sudden leaf drop", "Wilting after draft"],
  transplant: ["Drooping after repot", "Sudden leaf drop", "Yellowing leaves", "Curling leaves", "Wilting despite moist soil", "Browning leaf edges", "Stalled growth"],
  physical: ["Snapped stem", "Torn leaves", "Crushed growth", "Broken roots", "Pot fell"],
  nutrient: ["Pale new growth", "Yellow between veins", "Purple tint", "Stunted growth", "Brown leaf tips"],
  other: ["Leaf drop", "Wilting", "Discoloration", "Stunted growth", "Unusual smell"],
};

export const CAUSES = {
  pest: ["Spider mites", "Thrips", "Mealybugs", "Scale", "Aphids", "Fungus gnats", "New plant brought in", "Dry air"],
  disease: ["Bacterial infection", "Fungal infection", "Wet leaves overnight", "Poor airflow", "Contaminated tools"],
  root: ["Overwatering", "Poor drainage", "Pot too large", "Dense soil", "Cold, wet soil"],
  overwatering: ["Watered too often", "No drainage hole", "Low light", "Dense soil", "Pot too large"],
  underwatering: ["Missed waterings", "Heat wave", "Root-bound", "Fast-draining soil", "Moved to brighter spot"],
  sunburn: ["Moved to direct sun", "Window glass magnification", "Grow light too close", "Summer heat"],
  cold: ["Cold draft", "Near AC vent", "Left outside", "Window in winter", "Shipping"],
  transplant: ["Recent repot", "Roots disturbed / trimmed", "Pot size jump", "Soil type change", "Moved to new spot", "Repotted in dormancy", "Fertilised too soon"],
  physical: ["Knocked over", "Pet damage", "Repotting accident", "Moving / transport"],
  nutrient: ["No fertilizer in months", "Old, depleted soil", "pH imbalance", "Over-fertilized"],
  other: ["Unknown", "Recent move", "Environmental change"],
};

export const AFFECTED = ["Leaves", "New growth", "Stem", "Roots", "Soil", "Flowers", "Whole plant"];

export const ACTIONS = ["Isolated plant", "Removed affected leaves", "Rinsed foliage", "Wiped leaves", "Let soil dry out", "Watered thoroughly", "Repotted", "Trimmed roots", "Moved location", "Nothing yet"];

export const TREATMENTS = {
  pest: ["Neem oil spray", "Insecticidal soap", "70% IPA wipe-down", "Systemic granules", "Sticky traps", "Predatory mites", "Weekly shower"],
  disease: ["Remove infected tissue", "Copper fungicide", "Improve airflow", "Water at soil only", "Sterilise tools"],
  root: ["Repot in fresh mix", "Trim rotten roots", "Hydrogen peroxide soak", "Switch to terracotta", "Water less often"],
  overwatering: ["Water less often", "Add drainage", "Chunkier soil", "Bottom water only", "Brighter spot"],
  underwatering: ["Soak thoroughly", "Shorter watering interval", "Humidity tray", "Self-watering pot"],
  sunburn: ["Move to bright indirect", "Sheer curtain", "Trim burned leaves"],
  cold: ["Move away from draft", "Warmer room", "Trim damaged leaves", "Reduce watering"],
  transplant: ["Keep out of direct sun", "Hold off fertilizer", "Keep soil lightly moist", "Raise humidity", "Don't move it again", "Trim stressed leaves", "Be patient (2–4 weeks)"],
  physical: ["Stake / support", "Clean cut below damage", "Propagate broken piece", "Cinnamon on wounds"],
  nutrient: ["Balanced fertilizer", "Fresh soil", "Cal-mag supplement", "Flush soil"],
  other: ["Monitor closely", "Adjust care routine"],
};

export const FOLLOW_UPS = [["1", "Tomorrow"], ["3", "In 3 days"], ["7", "In 1 week"], ["14", "In 2 weeks"], ["custom", "Pick a date"]];

export const plusDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + Number(n));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const joinText = (chips, text) => [chips.join(", "), text.trim()].filter(Boolean).join(". ") || null;

export const LIGHT = [["low", "Low light"], ["medium", "Medium"], ["bright_indirect", "Bright indirect"], ["direct", "Direct sun"], ["grow_light", "Grow light"]];
export const LIGHT_LABELS = Object.fromEntries(LIGHT);
export const POT_SIZES = ['2"', '3"', '4"', '5"', '6"', '8"', '10"', '12"', '14"+'];
export const POT_MATERIALS = ["Nursery plastic", "Terracotta", "Ceramic", "Glazed ceramic", "Self-watering", "Fabric", "Hanging basket", "Glass / LECA"];
export const SOILS = ["Aroid mix", "Cactus / succulent", "All-purpose potting", "Orchid bark", "Peat-free", "LECA / semi-hydro", "Sphagnum moss", "Custom mix"];
export { Drop };
